import { useState, useEffect } from 'react';
import Spinner from '@/components/ui/Spinner';
import Button from '@/components/ui/Button';
import MultiSelect from '@/components/ui/MultiSelect';
import WatchlistMultiFilter from './WatchlistMultiFilter';
import { ESMStockFilterOptions, ESMTransactionType } from '@/types/esmStock';

interface ESMStockFilterFormProps {
  filterOptions: ESMStockFilterOptions;
  onSubmit: (filters: Record<string, unknown>) => void;
  isLoading?: boolean;
}

export default function ESMStockFilterForm({ filterOptions, onSubmit, isLoading = false }: ESMStockFilterFormProps) {
  // Get default dates (today and 30 days ago)
  const getDefaultDates = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    const tomorrow = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    tomorrow.setDate(today.getDate() + 1);
    
    return {
      end_date: today.toISOString().split('T')[0], // YYYY-MM-DD format
      start_date: thirtyDaysAgo.toISOString().split('T')[0],
      tomorrow: tomorrow.toISOString().split('T')[0]
    };
  };

  const defaultDates = getDefaultDates();

  const [filters, setFilters] = useState({
    stock_name: filterOptions.stock_name || '',
    start_date: filterOptions.start_date || defaultDates.start_date,
    end_date: filterOptions.end_date || defaultDates.end_date,
    transaction_type: filterOptions.transaction_type || ESMTransactionType.entry,
    is_prediction: false,
    is_sme: filterOptions.is_sme !== undefined ? filterOptions.is_sme : '',
    should_put_price_filter: filterOptions.should_put_price_filter !== undefined ? filterOptions.should_put_price_filter : true,
    listing_types: [] as string[],
    watchlist_ids: '',
    pe_min: '',
    pe_max: '',
  });

  useEffect(() => {
    // Update start date to tomorrow when is_prediction is true
    if (filters.is_prediction) {
      setFilters(prev => ({ 
        ...prev, 
        start_date: defaultDates.tomorrow,
        transaction_type: filters.transaction_type === ESMTransactionType.exit || 
                         filters.transaction_type === ESMTransactionType.moved_down ? 
                         filters.transaction_type : ESMTransactionType.exit 
      }));
    }
  }, [filters.is_prediction, defaultDates.tomorrow, filters.transaction_type]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === "is_prediction") {
      setFilters(prev => ({ ...prev, [name]: value === "true" }));
    } else if (name === "is_sme" && value === "") {
      setFilters(prev => ({ ...prev, [name]: value }));
    } else if (name === "is_sme") {
      setFilters(prev => ({ ...prev, [name]: value === "true" }));
    } else if (name === "should_put_price_filter") {
      setFilters(prev => ({ ...prev, [name]: value === "true" }));
    } else {
      setFilters(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleReset = () => {
    const defaultDates = getDefaultDates();
    setFilters({
      stock_name: '',
      start_date: defaultDates.start_date,
      end_date: defaultDates.end_date,
      transaction_type: ESMTransactionType.entry,
      is_prediction: false,
      is_sme: '',
      should_put_price_filter: true,
      listing_types: [],
      watchlist_ids: '',
      pe_min: '',
      pe_max: '',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Convert string values to appropriate types
    const processedFilters = {
      stock_name: filters.stock_name || undefined,
      start_date: filters.start_date || undefined,
      end_date: filters.is_prediction ? undefined : (filters.end_date || undefined),
      transaction_type: filters.transaction_type,
      is_prediction: filters.is_prediction,
      is_sme: filters.is_sme === '' ? undefined : filters.is_sme,
      should_put_price_filter: filters.is_prediction ? filters.should_put_price_filter : undefined,
      listing_types: filters.listing_types.length > 0 ? filters.listing_types.join(',') : undefined,
      watchlist_ids: filters.watchlist_ids || undefined,
      pe_min: filters.pe_min || undefined,
      pe_max: filters.pe_max || undefined,
    };
    
    onSubmit(processedFilters);
  };

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Stock Name */}
          <div className="space-y-2">
            <label htmlFor="stock_name" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Stock Name</label>
            <input
              id="stock_name"
              name="stock_name"
              value={filters.stock_name}
              onChange={handleChange}
              placeholder="Enter stock name (optional)"
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Leave empty to search all stocks
            </p>
          </div>

          {/* Watchlist Filter */}
          <div className="space-y-2">
            <WatchlistMultiFilter
              value={filters.watchlist_ids}
              onChange={handleChange}
              watchlists={filterOptions.watchlists || []}
              label="Watchlists"
              placeholder="Select watchlists..."
            />
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Filter by stocks in selected watchlists
            </p>
          </div>
          
          {/* Is Prediction */}
          <div className="space-y-2">
            <label htmlFor="is_prediction" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Search Mode</label>
            <select
              id="is_prediction"
              name="is_prediction"
              value={filters.is_prediction.toString()}
              onChange={handleChange}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            >
              <option value="false">Historical Data</option>
              <option value="true">Prediction</option>
            </select>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Select mode for historical data or predictions
            </p>
          </div>
          
          {/* Start Date */}
          <div className="space-y-2">
            <label htmlFor="start_date" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {filters.is_prediction ? "Prediction Date" : "Start Date"}
            </label>
            <input
              id="start_date"
              name="start_date"
              type="date"
              value={filters.start_date}
              onChange={handleChange}
              min={filters.is_prediction ? defaultDates.tomorrow : undefined}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {filters.is_prediction ? 
                "Date for predicting future transactions" : 
                "Start date for historical ESM transactions"}
            </p>
          </div>
          
          {/* End Date - only show if is_prediction is false */}
          {!filters.is_prediction && (
            <div className="space-y-2">
              <label htmlFor="end_date" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">End Date</label>
              <input
                id="end_date"
                name="end_date"
                type="date"
                value={filters.end_date}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                End date for ESM transactions
              </p>
            </div>
          )}
          
          {/* Transaction Type */}
          <div className="space-y-2">
            <label htmlFor="transaction_type" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Transaction Type</label>
            <select
              id="transaction_type"
              name="transaction_type"
              value={filters.transaction_type}
              onChange={handleChange}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            >
              {!filters.is_prediction && <option value={ESMTransactionType.entry}>Entry</option>}
              <option value={ESMTransactionType.exit}>Exit</option>
              {!filters.is_prediction && <option value={ESMTransactionType.moved_up}>ESM 1-&gt;2</option>}
              <option value={ESMTransactionType.moved_down}>ESM 2-&gt;1</option>
            </select>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Type of ESM transaction
            </p>
          </div>
          
          {/* SME Filter */}
          <div className="space-y-2">
            <label htmlFor="is_sme" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">SME Status</label>
            <select
              id="is_sme"
              name="is_sme"
              value={typeof filters.is_sme === 'boolean' ? filters.is_sme.toString() : ''}
              onChange={handleChange}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            >
              <option value="">All Companies</option>
              <option value="true">SME Only</option>
              <option value="false">Non-SME Only</option>
            </select>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Filter by SME (Small and Medium Enterprises) status
            </p>
          </div>

          {/* Listing Types Filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Listing Types</label>
            <MultiSelect
              options={['Rights', 'Preference', 'Open Offer']}
              selected={filters.listing_types}
              onChange={(selected) => setFilters({...filters, listing_types: selected})}
              placeholder="All Listing Types"
              className="w-full"
              maxDisplayItems={1}
            />
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Filter by listing types (Rights, Preference, Open Offer)
            </p>
          </div>

          {/* PE Ratio Range Filter */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">P/E Ratio Range</label>
            <div className="flex gap-2">
              <input
                type="number"
                name="pe_min"
                value={filters.pe_min || ''}
                onChange={handleChange}
                placeholder="Min P/E"
                step="0.1"
                min="0"
                className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
              <input
                type="number"
                name="pe_max"
                value={filters.pe_max || ''}
                onChange={handleChange}
                placeholder="Max P/E"
                step="0.1"
                min="0"
                className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
            </div>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Filter stocks by P/E ratio range (frontend filter)
            </p>
          </div>
          
          {/* Price Filter - only show if is_prediction is true */}
          {filters.is_prediction && (
            <div className="space-y-2">
              <label htmlFor="should_put_price_filter" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Price Filter</label>
              <select
                id="should_put_price_filter"
                name="should_put_price_filter"
                value={filters.should_put_price_filter.toString()}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              >
                <option value="true">Include Price Filter</option>
                <option value="false">Exclude Price Filter</option>
              </select>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Include or exclude price-based filtering in predictions
              </p>
            </div>
          )}
        </div>
        
        <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-zinc-200 dark:border-zinc-800">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={isLoading}
          >
            Reset
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Searching...
              </>
            ) : (
              'Search'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
} 